import React, { useState, useEffect } from 'react';
import { getPropertyDetail, calculateCommute } from '../api/chat';
import { toast } from 'react-toastify';

const PropertyDetailModal = ({ isOpen, item, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    
    // Transition States
    const [renderState, setRenderState] = useState("closed"); // "closed", "opening", "open", "closing"
    const [activeItem, setActiveItem] = useState(null);

    // Commute Calculator State
    const [commuteDest, setCommuteDest] = useState("");
    const [commuteMode, setCommuteMode] = useState("driving");
    const [commuteResult, setCommuteResult] = useState(null);
    const [commuteLoading, setCommuteLoading] = useState(false);
    const [useTransit, setUseTransit] = useState(false);

    // Handle Opening/Closing Transitions
    useEffect(() => {
        if (isOpen && item) {
            setActiveItem(item);
            setRenderState("opening");
            const timer = setTimeout(() => setRenderState("open"), 30);
            return () => clearTimeout(timer);
        } else if (!isOpen && (renderState === "open" || renderState === "opening")) {
            setRenderState("closing");
            const timer = setTimeout(() => {
                setRenderState("closed");
                setActiveItem(null);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, item]);

    // Fetch Details on Item Load
    useEffect(() => {
        if (!activeItem) return;

        setLoading(true);
        setDetail(null);
        setActiveIndex(0);
        setCommuteDest("");
        setCommuteMode("driving");
        setCommuteResult(null);
        setCommuteLoading(false);
        setUseTransit(false);

        const fetchDetail = async () => {
            try {
                const data = await getPropertyDetail(
                    activeItem.url,
                    activeItem.sumber,
                    activeItem.lokasi || "",
                    activeItem.latitude || 0,
                    activeItem.longitude || 0,
                    activeItem.image || ""
                );
                
                setDetail({
                    ...data,
                    images: Array.isArray(data.images) ? data.images : [],
                    description: data.description || '',
                    bedrooms: data.bedrooms || 0,
                    bathrooms: data.bathrooms || 0,
                    land_area: data.land_area || 0,
                    building_area: data.building_area || 0,
                    facilities: Array.isArray(data.facilities) ? data.facilities : [],
                    safety_analysis: data.safety_analysis || null,
                    transit_analysis: data.transit_analysis || null
                });
            } catch (e) {
                console.error(e);
                toast.error("Gagal memuat detail properti.");
                onClose();
            } finally {
                setLoading(false);
            }
        };

        fetchDetail();
    }, [activeItem]);

    // Handle Leaflet Map Initialization for Commute Route
    useEffect(() => {
        if (!commuteResult || !commuteResult.route_coords) return;

        let mapInstance = null;
        let animInterval = null;

        const initLeafletMap = () => {
            const L = window.L;
            if (!L) return;

            const mapContainer = document.getElementById("commute-leaflet-map");
            if (!mapContainer) return;
            mapContainer.innerHTML = "";

            const origin = commuteResult.origin;
            const dest = commuteResult.destination;
            const coords = commuteResult.route_coords;

            const polylinePoints = coords.map(c => [c[0], c[1]]);

            mapInstance = L.map("commute-leaflet-map", {
                zoomControl: true
            }).setView([origin.lat, origin.lon], 14);

            L.tileLayer('https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap contributors'
            }).addTo(mapInstance);

            const originIcon = L.divIcon({
                html: '<span style="font-size: 24px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">📍</span>',
                className: 'leaflet-origin-marker',
                iconSize: [24, 24],
                iconAnchor: [12, 24]
            });

            const destIcon = L.divIcon({
                html: '<span style="font-size: 24px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">🏢</span>',
                className: 'leaflet-dest-marker',
                iconSize: [24, 24],
                iconAnchor: [12, 12]
            });

            L.marker([origin.lat, origin.lon], { icon: originIcon })
                .addTo(mapInstance)
                .bindPopup(`<b>Asal:</b> ${origin.address}`);

            L.marker([dest.lat, dest.lon], { icon: destIcon })
                .addTo(mapInstance)
                .bindPopup(`<b>Tujuan:</b> ${dest.address}`);

            const polyline = L.polyline(polylinePoints, { 
                color: '#16a34a',
                weight: 4,
                opacity: 0.8
            }).addTo(mapInstance);

            mapInstance.fitBounds(polyline.getBounds(), { padding: [20, 20] });

            // GPS Travel Simulation Dot
            if (polylinePoints.length > 0) {
                const pulseIcon = L.divIcon({
                    html: '<div class="gps-pulse-dot"></div>',
                    className: 'leaflet-gps-pulse',
                    iconSize: [12, 12],
                    iconAnchor: [6, 6]
                });
                const pulseMarker = L.marker(polylinePoints[0], { icon: pulseIcon }).addTo(mapInstance);

                let pointIndex = 0;
                animInterval = setInterval(() => {
                    if (polylinePoints.length === 0) return;
                    pointIndex = (pointIndex + 1) % polylinePoints.length;
                    pulseMarker.setLatLng(polylinePoints[pointIndex]);
                }, 100);
            }
        };

        if (!window.L) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);

            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.onload = () => {
                setTimeout(initLeafletMap, 50);
            };
            document.head.appendChild(script);
        } else {
            setTimeout(initLeafletMap, 50);
        }

        return () => {
            if (animInterval) {
                clearInterval(animInterval);
            }
            if (mapInstance) {
                mapInstance.remove();
            }
        };
    }, [commuteResult]);

    if (renderState === "closed" || !activeItem) return null;

    const nextImage = () => {
        if (detail && detail.images && detail.images.length > 0) {
            setActiveIndex((prev) => (prev + 1) % detail.images.length);
        }
    };

    const prevImage = () => {
        if (detail && detail.images && detail.images.length > 0) {
            setActiveIndex((prev) => (prev - 1 + detail.images.length) % detail.images.length);
        }
    };

    const handleCalculateCommute = async (e) => {
        e.preventDefault();
        if (!commuteDest || commuteDest.trim() === "") return;
        setCommuteLoading(true);
        setCommuteResult(null);
        try {
            const origin = (detail && detail.location) || activeItem.lokasi || activeItem.nama_tempat;
            const mode = (commuteMode === 'walking' && useTransit) ? 'transit' : commuteMode;
            const res = await calculateCommute(origin, commuteDest, mode);
            if (res.status === "success") {
                setCommuteResult(res);
            } else {
                toast.error(res.message || "Gagal menghitung rute, coba lagi.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Gagal menghubungi server komuter.");
        } finally {
            setCommuteLoading(false);
        }
    };

    const displayImages = loading ? [] : (detail?.images || []);

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.6)',
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                opacity: (renderState === "opening" || renderState === "closing") ? 0 : 1,
                transition: 'opacity 250ms ease-in-out',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#fff',
                    borderRadius: 16,
                    width: '100%',
                    maxWidth: 420,
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    position: 'relative',
                    transform: (renderState === "opening" || renderState === "closing") ? 'translateY(24px) scale(0.97)' : 'translateY(0) scale(1)',
                    opacity: (renderState === "opening" || renderState === "closing") ? 0 : 1,
                    transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 300ms ease-out',
                }}
                onClick={e => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    data-test-id="detail-modal-close-button"
                    style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        background: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        cursor: 'pointer',
                        zIndex: 10,
                        fontSize: 16,
                    }}
                >
                    ✕
                </button>

                <div style={{ position: 'relative', width: '100%', height: 220, background: '#f1f1f1' }}>
                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            Loading...
                        </div>
                    ) : displayImages.length > 0 ? (
                        <>
                            <img
                                src={displayImages[activeIndex]}
                                alt="Property"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            {displayImages.length > 1 && (
                                <>
                                    <button
                                        onClick={prevImage}
                                        data-test-id="detail-modal-prev-image-button"
                                        style={{
                                            position: 'absolute',
                                            left: 10,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'rgba(0,0,0,0.5)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: 36,
                                            height: 36,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ‹
                                    </button>
                                    <button
                                        onClick={nextImage}
                                        data-test-id="detail-modal-next-image-button"
                                        style={{
                                            position: 'absolute',
                                            right: 10,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'rgba(0,0,0,0.5)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: 36,
                                            height: 36,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ›
                                    </button>
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 10,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        background: 'rgba(0,0,0,0.5)',
                                        color: '#fff',
                                        borderRadius: 12,
                                        padding: '2px 10px',
                                        fontSize: 12,
                                    }}>
                                        {activeIndex + 1} / {displayImages.length}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aaa', fontSize: 48 }}>
                            🏠
                        </div>
                    )}
                </div>

                <div style={{ padding: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{activeItem.nama_tempat}</div>
                    <div style={{ fontSize: 14, color: '#16a34a', fontWeight: 600, marginBottom: 4 }}>{activeItem.harga}</div>
                    <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>📍 {detail?.location || activeItem.lokasi}</div>
                    
                    {!loading && detail && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                            {detail.bedrooms > 0 && (
                                <span style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '3px 10px', fontSize: 12, color: '#15803d' }}>
                                    🛌 {detail.bedrooms} Kamar Tidur
                                </span>
                            )}
                            {detail.bathrooms > 0 && (
                                <span style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '3px 10px', fontSize: 12, color: '#15803d' }}>
                                    🚿 {detail.bathrooms} Kamar Mandi
                                </span>
                            )}
                            {detail.building_area > 0 && (
                                <span style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '3px 10px', fontSize: 12, color: '#15803d' }}>
                                    🏠 {detail.building_area} m² Bangunan
                                </span>
                            )}
                            {detail.land_area > 0 && (
                                <span style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '3px 10px', fontSize: 12, color: '#15803d' }}>
                                    📐 {detail.land_area} m² Tanah
                                </span>
                            )}
                        </div>
                    )}

                    {!loading && detail && detail.facilities.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Fasilitas</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {detail.facilities.map((f, i) => (
                                    <span key={i} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 8px', fontSize: 11, color: '#374151' }}>
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {(loading || (detail && detail.description)) && (
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>Deskripsi</div>
                            <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                {loading ? 'Loading detail...' : detail.description}
                            </div>
                        </div>
                    )}

                    {!loading && detail && detail.facilities.length === 0 && detail.bedrooms === 0 && !detail.description && (
                        <div style={{ fontSize: 13, color: '#999', textAlign: 'center', padding: '12px 0' }}>Tidak ada detail tersedia.</div>
                    )}

                    {/* 1. Safety & Flood Analysis Section */}
                    {!loading && detail && detail.safety_analysis && Object.keys(detail.safety_analysis).length > 0 && (
                        <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                🛡️ Analisis Keamanan & Risiko Banjir
                            </div>
                            
                            {/* Risk Badge and Elevation */}
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                <span 
                                    className={detail.safety_analysis.flood_risk_level === 'Tinggi' ? 'pulse-high-risk' : ''}
                                    style={{ 
                                        background: detail.safety_analysis.flood_risk_level === 'Tinggi' ? '#fef2f2' : (detail.safety_analysis.flood_risk_level === 'Sedang' ? '#fffbeb' : '#f0fdf4'), 
                                        border: `1px solid ${detail.safety_analysis.flood_risk_level === 'Tinggi' ? '#fecaca' : (detail.safety_analysis.flood_risk_level === 'Sedang' ? '#fef3c7' : '#bbf7d0')}`, 
                                        borderRadius: 8, 
                                        padding: '4px 10px', 
                                        fontSize: 11, 
                                        fontWeight: 600,
                                        color: detail.safety_analysis.flood_risk_level === 'Tinggi' ? '#dc2626' : (detail.safety_analysis.flood_risk_level === 'Sedang' ? '#d97706' : '#16a34a')
                                    }}
                                >
                                    Risiko Banjir: {detail.safety_analysis.flood_risk_level || 'Tidak Diketahui'}
                                </span>
                                <span style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 10px', fontSize: 11, color: '#4b5563' }}>
                                    🏔️ {typeof detail.safety_analysis.elevation_meters === 'number' ? detail.safety_analysis.elevation_meters.toFixed(1) : '0.0'} mdpl
                                </span>
                            </div>

                            {/* Summary text */}
                            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.5, marginBottom: 8 }}>
                                {detail.safety_analysis.flood_analysis_summary}
                            </div>

                            {/* Recent news */}
                            {detail.safety_analysis.recent_flood_news && detail.safety_analysis.recent_flood_news.length > 0 && (
                                <div style={{ background: '#fafafa', borderRadius: 8, padding: 8, border: '1px dashed #ddd', marginTop: 6 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#666', marginBottom: 4 }}>Berita Terkait Sekitar Lokasi:</div>
                                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: '#333' }}>
                                        {detail.safety_analysis.recent_flood_news.map((news, nIdx) => (
                                            <li key={nIdx} style={{ marginBottom: 4 }}>
                                                <a href={news.link} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
                                                    {news.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 1.5. Nearby Public Transit Stations */}
                    {!loading && detail && detail.transit_analysis && (
                        <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                🚌 Transportasi Umum Terdekat
                            </div>
                            {detail.transit_analysis.transit_found && Array.isArray(detail.transit_analysis.stations) && detail.transit_analysis.stations.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {detail.transit_analysis.stations.map((st, sIdx) => {
                                        if (!st) return null;
                                        const stType = st.type || '';
                                        return (
                                            <div key={sIdx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', fontSize: 11 }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{st.name || 'Stasiun/Halte'}</span>
                                                    <span style={{ fontSize: 10, color: '#64748b' }}>
                                                        {stType === 'Stasiun MRT' ? '🚇 MRT' : (stType === 'Stasiun LRT' ? '🚄 LRT' : (stType.includes('KRL') ? '🚆 KRL Commuter' : '🚌 TransJakarta'))}
                                                    </span>
                                                </div>
                                                <div style={{ textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>
                                                    <div>📏 {st.distance_meters || 0} m</div>
                                                    <div style={{ fontSize: 9, color: '#64748b', fontWeight: 500 }}>🚶 {st.walk_duration_minutes || 0} mnt jalan</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic', paddingLeft: 4 }}>
                                    Tidak ada stasiun KRL/LRT/MRT atau halte TransJakarta terdeteksi dalam radius 2 km.
                                </div>
                            )}
                        </div>
                    )}

                    {/* 2. Travel Commute Check Section */}
                    {!loading && (
                        <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                🚗 Cek Jarak ke Tempat Kerja / Kampus
                            </div>
                            
                            <form onSubmit={handleCalculateCommute} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                                <input
                                    type="text"
                                    value={commuteDest}
                                    onChange={(e) => setCommuteDest(e.target.value)}
                                    data-test-id="commute-destination-input"
                                    placeholder="Masukkan nama kantor, kampus, atau alamat..."
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: 12,
                                        border: '1.5px solid #16a34a',
                                        borderRadius: 8,
                                        outline: 'none',
                                        fontFamily: 'Rubik, Arial, sans-serif'
                                    }}
                                    required
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <select
                                            value={commuteMode}
                                            data-test-id="commute-mode-select"
                                            onChange={(e) => {
                                                setCommuteMode(e.target.value);
                                                if (e.target.value !== 'walking') {
                                                    setUseTransit(false);
                                                }
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: '6px 10px',
                                                fontSize: 12,
                                                border: '1px solid #ccc',
                                                borderRadius: 8,
                                                backgroundColor: '#fff',
                                                fontFamily: 'Rubik, Arial, sans-serif'
                                            }}
                                        >
                                            <option value="driving">🚗 Berkendara (Mobil/Motor)</option>
                                            <option value="walking">🚶 Jalan Kaki</option>
                                            <option value="cycling">🚲 Sepeda</option>
                                        </select>
                                        <button
                                            type="submit"
                                            disabled={commuteLoading}
                                            data-test-id="check-commute-route-button"
                                            style={{
                                                flex: 1,
                                                background: '#16a34a',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                cursor: commuteLoading ? 'not-allowed' : 'pointer',
                                                padding: '6px 10px'
                                            }}
                                        >
                                            {commuteLoading ? 'Menghitung...' : 'Cek Rute'}
                                        </button>
                                    </div>
                                    {commuteMode === 'walking' && (
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer', color: '#4b5563', paddingLeft: 2 }}>
                                            <input
                                                type="checkbox"
                                                checked={useTransit}
                                                onChange={(e) => setUseTransit(e.target.checked)}
                                                data-test-id="use-transit-commute-checkbox"
                                                style={{ accentColor: '#16a34a', cursor: 'pointer' }}
                                            />
                                            Gunakan Transportasi Umum (KRL/MRT/TJ/Angkot)
                                        </label>
                                    )}
                                </div>
                            </form>

                            {/* Results */}
                            {commuteResult && (
                                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 10, marginTop: 8 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Rute Ditemukan!</span>
                                        <span style={{ fontSize: 10, color: '#666' }}>Mode: {commuteResult.mode === 'walking' ? 'Jalan Kaki' : (commuteResult.mode === 'cycling' ? 'Sepeda' : (commuteResult.mode === 'transit' ? 'Transportasi Umum' : 'Berkendara'))}</span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 12 }}>
                                        <div>📏 Jarak: <strong>{typeof commuteResult.distance_km === 'number' ? commuteResult.distance_km.toFixed(2) : '0.00'} km</strong></div>
                                        <div>⏱️ Waktu: <strong>{typeof commuteResult.duration_minutes === 'number' ? commuteResult.duration_minutes.toFixed(1) : '0.0'} menit</strong></div>
                                    </div>
                                    {commuteResult.route_coords ? (
                                        <div 
                                            className="animate-map"
                                            style={{ width: '100%', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', marginTop: 6 }}
                                        >
                                            <div id="commute-leaflet-map" style={{ width: '100%', height: '280px', background: '#f8fafc', zIndex: 1 }} />
                                        </div>
                                    ) : commuteResult.map_image_base64 ? (
                                        <div 
                                            className="animate-map"
                                            style={{ width: '100%', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 6px rgba(0,0,0,0.1)', marginTop: 6, position: 'relative' }}
                                        >
                                            <img
                                                src={commuteResult.map_image_base64}
                                                alt="Peta Komuter"
                                                style={{ width: '100%', height: 'auto', display: 'block' }}
                                            />
                                            {/* GPS Animation Overlay */}
                                            {commuteResult.route_pixels && commuteResult.route_pixels.length >= 2 && (
                                                <svg 
                                                    viewBox="0 0 500 400" 
                                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                                                >
                                                    {(() => {
                                                        const pathStr = `M ${commuteResult.route_pixels[0][0]} ${commuteResult.route_pixels[0][1]} ` + 
                                                                        commuteResult.route_pixels.slice(1).map(p => `L ${p[0]} ${p[1]}`).join(' ');
                                                        return (
                                                            <>
                                                                {/* Pulsing radar halo */}
                                                                <circle r="12" fill="none" stroke="#16a34a" strokeWidth="2" opacity="0.8">
                                                                    <animateMotion dur="6s" repeatCount="indefinite" path={pathStr} />
                                                                    <animate attributeName="r" values="6;20;6" dur="1.8s" repeatCount="indefinite" />
                                                                    <animate attributeName="opacity" values="0.8;0;0.8" dur="1.8s" repeatCount="indefinite" />
                                                                </circle>
                                                                {/* Moving GPS Dot */}
                                                                <circle r="6" fill="#16a34a" stroke="#fff" strokeWidth="2">
                                                                    <animateMotion dur="6s" repeatCount="indefinite" path={pathStr} />
                                                                </circle>
                                                            </>
                                                        );
                                                    })()}
                                                </svg>
                                            )}
                                        </div>
                                    ) : null}

                                    {/* Transit Route Step-by-Step */}
                                    {commuteResult.transit_steps && commuteResult.transit_steps.length > 0 && (
                                        <div style={{ marginTop: 12, borderTop: '1px dashed #bbf7d0', paddingTop: 8 }}>
                                            <div style={{ fontSize: 11, fontWeight: 600, color: '#15803d', marginBottom: 6 }}>Rincian Perjalanan (Step-by-Step):</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {commuteResult.transit_steps.map((step, idx) => {
                                                    let icon = '🚌';
                                                    const instLower = step.instruction.toLowerCase();
                                                    if (step.mode === 'walking' || instLower.includes('jalan kaki') || instLower.includes('berjalan')) icon = '🚶';
                                                    else if (step.mode === 'waiting' || instLower.includes('tunggu') || instLower.includes('menunggu')) icon = '⏱️';
                                                    else if (instLower.includes('mrt')) icon = '🚇';
                                                    else if (instLower.includes('krl') || instLower.includes('kereta')) icon = '🚆';
                                                    else if (instLower.includes('lrt')) icon = '🚄';
                                                    
                                                    return (
                                                        <div key={idx} style={{ display: 'flex', gap: 8, fontSize: 11, alignItems: 'flex-start' }}>
                                                            <span style={{ fontSize: 12, marginTop: 1 }}>{icon}</span>
                                                            <div style={{ flex: 1, color: '#374151' }}>
                                                                <div style={{ fontWeight: 500 }}>{step.instruction}</div>
                                                                <div style={{ fontSize: 9, color: '#6b7280', marginTop: 1 }}>
                                                                    {step.duration_minutes} mnt • {step.distance_meters >= 1000 ? `${(step.distance_meters/1000).toFixed(1)} km` : `${step.distance_meters} m`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PropertyDetailModal;
